import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/mockData';
import './index.css';

const UserCenter = () => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [userPosts, setUserPosts] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [editData, setEditData] = useState({});
  const [activeTab, setActiveTab] = useState('overview');
  const [popularPosts, setPopularPosts] = useState([]);
  
  // 上传相关状态
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // 新增：删除文章相关状态
  const [deletingPostId, setDeletingPostId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);
  
  // 使用 ref 来引用文件输入元素
  const coverFileInputRef = useRef(null);
  const avatarFileInputRef = useRef(null);

  // 获取热门文章 - 使用 useCallback
  const fetchPopularPosts = useCallback(async () => {
    try {
      const response = await api.getPopularPosts({ limit: 6 });
      if (response.success) {
        setPopularPosts(response.data);
      }
    } catch (error) {
      console.error('获取热门文章失败:', error);
    }
  }, []);

  // 获取用户的文章 - 使用 useCallback
  const fetchUserPosts = useCallback(async () => {
    try {
      // 先确保有用户数据
      if (!userData || !userData.id) {
        console.log('等待用户数据...');
        return;
      }
      
      const response = await api.getPosts({ 
        sort: 'latest',
        limit: 50
      });
      
      if (response.success) {
        const allPosts = response.data;
        
        // 使用当前登录用户的真实ID
        const currentUserId = userData.id;
        const filteredPosts = allPosts.filter(post => post.authorId === currentUserId);
        setUserPosts(filteredPosts);
        
        // 获取最近3篇文章
        const recent = filteredPosts.slice(0, 3);
        setRecentPosts(recent);
      }
    } catch (error) {
      console.error('获取用户文章失败:', error);
    }
  }, [userData]); // 依赖 userData

  // 获取用户信息 - 使用 useCallback
  const fetchUserData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.getCurrentUser();
      console.log('获取用户数据响应:', response);  // 添加这行
      
      if (response.success) {
        const user = response.data;

      console.log('当前用户ID:', user.id); // 添加这行
      console.log('当前用户名:', user.name);
      console.log('用户统计信息:', user.stats); // 添加这行
      console.log('用户加入日期:', user.joinDate, 'joinedAt:', user.joinedAt);

       // 强制设置新用户文章数为0
      if (user.stats && user.stats.posts > 0 && user.id > 1) { // 假设ID 1和2是预设用户
        console.log('检测到新用户有文章，强制设置为0');
        user.stats.posts = 0;
      }
        
        // 如果用户没有封面图，设置一个默认的猫咪封面
        if (!user.coverImage) {
          user.coverImage = 'https://images.unsplash.com/photo-1513360371669-4adf3dd7dff8?w=1200&h=300&fit=crop';
        }

        // 如果是新用户（ID大于1），重置所有统计数据
      if (user.id > 1) {
        console.log('新用户，重置统计数据');
        user.stats = {
          posts: 0,
          likes: 0,
          bookmarks: 0,
          totalViews: 0
        };

        // 新用户默认没有猫咪
        user.catCount = 0;
        user.catNames = '';
      }
        
      // 如果用户没有封面图，设置一个默认的猫咪封面
      if (!user.coverImage) {
        user.coverImage = 'https://images.unsplash.com/photo-1513360371669-4adf3dd7dff8?w=1200&h=300&fit=crop';
      }
      // 统一字段名：优先使用 joinedAt，如果都没有则使用当前日期
      if (!user.joinDate && !user.joinedAt) {
        // 如果既没有 joinDate 也没有 joinedAt，使用当前日期
        user.joinDate = new Date().toISOString().split('T')[0];
      } else if (user.joinedAt && !user.joinDate) {
        // 如果有 joinedAt 但没有 joinDate，统一为 joinDate
        user.joinDate = user.joinedAt;
      } else if (user.joinDate && !user.joinedAt) {
        // 如果有 joinDate 但没有 joinedAt，统一为 joinedAt
        user.joinedAt = user.joinDate;
      }
        
        
        setUserData(user);
        setEditData(user);
      }
    } catch (error) {
      console.error('获取用户数据失败:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // 从虚拟后端获取用户数据
  useEffect(() => {
    fetchUserData();
    fetchPopularPosts();
  }, [fetchUserData, fetchPopularPosts]);

  // 当 userData 更新时获取用户的文章
  useEffect(() => {
    if (userData && userData.id) {
      fetchUserPosts();
    }
  }, [userData, fetchUserPosts]);

  useEffect(() => {
    // 检查 localStorage 中是否有用户信息
    const currentUser = localStorage.getItem('cat_blog_current_user');
    if (!currentUser) {
      navigate('/auth');
    }
  }, [navigate]);

  // 处理封面图片上传
  const handleCoverImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // 检查文件类型
      if (!file.type.startsWith('image/')) {
        alert('请选择图片文件！');
        return;
      }
      
      // 检查文件大小（限制5MB）
      if (file.size > 5 * 1024 * 1024) {
        alert('图片大小不能超过5MB！');
        return;
      }
      
      try {
        setIsUploadingCover(true);
        setUploadProgress(0);
        
        // 模拟上传进度
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => {
            if (prev >= 90) {
              clearInterval(progressInterval);
              return 90;
            }
            return prev + 10;
          });
        }, 200);
        
        // 调用虚拟后端API上传图片
        const response = await api.uploadImage(file, 'cover');
        clearInterval(progressInterval);
        
        if (response.success) {
          setUploadProgress(100);
          const newCoverUrl = response.data.url;
          
          // 更新用户封面
          const updateResponse = await api.updateUserCover(userData.id, newCoverUrl);
          
          if (updateResponse.success) {
            // 更新本地状态
            setUserData(prev => ({ ...prev, coverImage: newCoverUrl }));
            
            // 延迟一点显示成功消息
            setTimeout(() => {
              setIsUploadingCover(false);
              setUploadProgress(0);
              alert('封面图片更新成功！');
            }, 300);
          }
        }
      } catch (error) {
        console.error('上传失败:', error);
        setIsUploadingCover(false);
        setUploadProgress(0);
        alert('上传失败，请重试！');
      }
    }
  };

  // 处理头像上传
  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // 检查文件类型
      if (!file.type.startsWith('image/')) {
        alert('请选择图片文件！');
        return;
      }
      
      // 检查文件大小（限制2MB）
      if (file.size > 2 * 1024 * 1024) {
        alert('头像大小不能超过2MB！');
        return;
      }
      
      try {
        setIsUploadingAvatar(true);
        
        // 调用虚拟后端API上传图片
        const response = await api.uploadImage(file, 'avatar');
        
        if (response.success) {
          const newAvatarUrl = response.data.url;
          
          // 更新用户头像
          const updateResponse = await api.updateUserAvatar(userData.id, newAvatarUrl);
          
          if (updateResponse.success) {
            // 更新本地状态
            setUserData(prev => ({ ...prev, avatar: newAvatarUrl }));
            setEditData(prev => ({ ...prev, avatar: newAvatarUrl }));
            
            setIsUploadingAvatar(false);
            alert('头像更新成功！');
          }
        }
      } catch (error) {
        console.error('上传失败:', error);
        setIsUploadingAvatar(false);
        alert('上传失败，请重试！');
      }
    }
  };

  // 触发封面图片选择
  const triggerCoverImageSelect = () => {
    if (coverFileInputRef.current) {
      coverFileInputRef.current.click();
    }
  };

  // 触发头像选择
  const triggerAvatarSelect = () => {
    if (avatarFileInputRef.current) {
      avatarFileInputRef.current.click();
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!editData.name || editData.name.trim() === '') {
      alert('请输入用户名！');
      return;
    }
    
    if (!editData.email || editData.email.trim() === '') {
      alert('请输入邮箱！');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // 准备更新的数据
      const updates = {
        name: editData.name,
        bio: editData.bio,
        email: editData.email,
        location: editData.location,
        catNames: editData.catNames
      };
      
      // 调用虚拟后端API更新用户信息
      const response = await api.updateUser(userData.id, updates);
      
      if (response.success) {
        // 更新本地状态
        setUserData(prev => ({
          ...prev,
          ...updates
        }));
        
        setIsEditing(false);
        setIsSubmitting(false);
        alert('个人资料已保存！');
      }
    } catch (error) {
      console.error('保存失败:', error);
      setIsSubmitting(false);
      alert('保存失败，请重试！');
    }
  };

  const handleCancel = () => {
    setEditData({ ...userData });
    setIsEditing(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 处理猫咪名字的显示
  const formatCatNames = (names) => {
    if (!names) return '还没有猫咪';
    return names;
  };

  // 格式化日期
  const formatDate = (dateString) => {
    if (!dateString) return '未知日期';
    try {
      const date = new Date(dateString);
      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
    } catch (error) {
      return dateString;
    }
  };

// 获取用户的统计数据
const getUserStats = () => {
  if (!userData) {
    return {
      posts: 0,
      likes: 0,
      bookmarks: 0,
      views: 0,
      fans: 0  // 新用户没有粉丝
    };
  }
  
  if (!userData.stats) {
    return {
      posts: userPosts.length,
      likes: 0,
      bookmarks: 0,
      views: 0,
      fans: 0
    };
  }
  
  return {
    posts: userData.stats.posts || 0,
    likes: userData.stats.likes || 0,
    bookmarks: userData.stats.bookmarks || 0,
    views: userData.stats.totalViews || 0,
    fans: 0  // 移除默认粉丝数，待后续实现
  };
};

  // 渲染分类统计
  const renderCategoryStats = () => {
    const categoryCounts = {};
    userPosts.forEach(post => {
      categoryCounts[post.category] = (categoryCounts[post.category] || 0) + 1;
    });
    
    return Object.entries(categoryCounts).map(([category, count]) => (
      <div key={category} className="category-stat">
        <span className="category-name">{category}</span>
        <span className="category-count">{count} 篇</span>
      </div>
    ));
  };

  // 新增：点击删除按钮，显示确认弹窗
  const handleDeleteClick = (post, e) => {
    e.stopPropagation();
    setPostToDelete(post);
    setShowDeleteConfirm(true);
  };

  // 新增：取消删除
  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setPostToDelete(null);
    setDeletingPostId(null);
  };

  // 新增：确认删除文章
  const confirmDeletePost = async () => {
    if (!postToDelete) return;
    
    try {
      setDeletingPostId(postToDelete.id);
      
      // 调用虚拟删除API
      const response = await api.deletePost(postToDelete.id);
      
      if (response.success) {
        // 从本地状态中移除删除的文章
        const updatedPosts = userPosts.filter(post => post.id !== postToDelete.id);
        setUserPosts(updatedPosts);
        
        // 更新最近文章列表
        setRecentPosts(updatedPosts.slice(0, 3));
        
        alert('文章删除成功！');
      } else {
        alert('文章删除失败，请重试！');
      }
    } catch (error) {
      console.error('删除文章失败:', error);
      alert('删除失败：' + (error.message || '网络错误，请重试'));
    } finally {
      setDeletingPostId(null);
      setShowDeleteConfirm(false);
      setPostToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="user-center-loading">
        <div className="loading-cat">
          <div className="cat-emoji">😺</div>
          <div className="loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <p>正在加载个人资料...</p>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="user-center-error">
        <div className="error-cat">😿</div>
        <h3>加载失败</h3>
        <p>无法加载用户数据，请稍后再试</p>
        <button onClick={fetchUserData} className="retry-btn">
          重新加载
        </button>
      </div>
    );
  }

  const stats = getUserStats();

  return (
    <div className="user-center">
      {/* 隐藏的文件输入元素 */}
      <input
        type="file"
        ref={coverFileInputRef}
        style={{ display: 'none' }}
        accept="image/*"
        onChange={handleCoverImageUpload}
        disabled={isUploadingCover}
      />
      <input
        type="file"
        ref={avatarFileInputRef}
        style={{ display: 'none' }}
        accept="image/*"
        onChange={handleAvatarUpload}
        disabled={isUploadingAvatar}
      />

      {/* 封面图 */}
      <div className="user-cover">
        <img 
          src={userData.coverImage} 
          alt="用户封面" 
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = 'https://images.unsplash.com/photo-1513360371669-4adf3dd7dff8?w=1200&h=300&fit=crop';
          }}
        />
        <div className="cover-overlay"></div>
        <div className="cover-actions">
          <button 
            className="cover-edit-btn"
            onClick={triggerCoverImageSelect}
            disabled={isUploadingCover}
          >
            {isUploadingCover ? (
              <>
                <span className="edit-icon">⏳</span>
                上传中... {uploadProgress}%
              </>
            ) : (
              <>
                <span className="edit-icon">📷</span>
                更换封面
              </>
            )}
          </button>
        </div>
      </div>

      <div className="user-container">
        {/* 侧边栏 */}
        <div className="user-sidebar">
          <div className="user-avatar">
            <img 
              src={userData.avatar} 
              alt="用户头像"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'https://images.unsplash.com/photo-1561948955-570b270e7c36?w=200&h=200&fit=crop&crop=face';
              }}
            />
            <div className="avatar-ring"></div>
            <button 
              className="avatar-edit-btn"
              onClick={triggerAvatarSelect}
              disabled={isUploadingAvatar}
            >
              {isUploadingAvatar ? (
                <span className="edit-icon">⏳</span>
              ) : (
                <span className="edit-icon">📷</span>
              )}
            </button>
          </div>
          
          {!isEditing ? (
            <div className="user-info-static">
              <h2 className="user-name">{userData.name || userData.username}</h2>
              <p className="user-title">{userData.bio || '爱猫人士'}</p>
              
              <div className="user-stats">
                <div className="stat-item">
                  <span className="stat-number">{stats.posts}</span>
                  <span className="stat-label">文章</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">{userData.catCount || 0}</span>
                  <span className="stat-label">猫咪</span>
                </div>
                 <div className="stat-item">
                  <span className="stat-number">{stats.likes}</span> {/* 使用真实点赞数 */}
                  <span className="stat-label">获赞</span>
                 </div>
                <div className="stat-item">
                  <span className="stat-number">{stats.fans}</span>
                  <span className="stat-label">粉丝</span>
                </div>
              </div>
              
              <div className="user-details">
                <div className="detail-item">
                  <span className="detail-icon">📧</span>
                  <span className="detail-text">{userData.email}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-icon">📍</span>
                  <span className="detail-text">{userData.location || '未知地区'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-icon">🐱</span>
                  <span className="detail-text">{formatCatNames(userData.catNames)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-icon">📅</span>
                  <span className="detail-text">加入于 {formatDate(userData.joinDate)}</span>
                </div>
              </div>
              
              <button className="edit-btn" onClick={handleEdit}>
                <span className="btn-icon">✏️</span>
                编辑资料
              </button>
            </div>
          ) : (
            <div className="user-info-edit">
              <h2 className="edit-title">编辑资料</h2>
              <div className="edit-form">
                <div className="form-group">
                  <label htmlFor="username">用户名 *</label>
                  <input
                    type="text"
                    id="username"
                    name="name"
                    value={editData.name || ''}
                    onChange={handleChange}
                    placeholder="输入用户名"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="bio">个人简介</label>
                  <textarea
                    id="bio"
                    name="bio"
                    value={editData.bio || ''}
                    onChange={handleChange}
                    rows="4"
                    placeholder="介绍一下你自己..."
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">邮箱 *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={editData.email || ''}
                    onChange={handleChange}
                    placeholder="输入邮箱地址"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="location">所在地</label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={editData.location || ''}
                    onChange={handleChange}
                    placeholder="输入所在城市"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="catNames">猫咪名字</label>
                  <input
                    type="text"
                    id="catNames"
                    name="catNames"
                    value={editData.catNames || ''}
                    onChange={handleChange}
                    placeholder="用顿号分隔猫咪名字"
                  />
                </div>
                
                <div className="form-actions">
                  <button 
                    className="save-btn" 
                    onClick={handleSave}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? '保存中...' : '保存更改'}
                  </button>
                  <button 
                    className="cancel-btn" 
                    onClick={handleCancel}
                    disabled={isSubmitting}
                  >
                    取消
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* 用户统计小卡片 */}
          <div className="sidebar-widget user-stats-card">
            <h3>📊 数据统计</h3>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-card-value">{stats.views}</div>
                <div className="stat-card-label">总阅读量</div>
              </div>
              <div className="stat-card">
                <div className="stat-card-value">{stats.likes}</div>
                <div className="stat-card-label">获赞数</div>
              </div>
              <div className="stat-card">
                <div className="stat-card-value">{stats.bookmarks}</div>
                <div className="stat-card-label">收藏数</div>
              </div>
            </div>
          </div>

          {/* 分类统计 */}
          {userPosts.length > 0 && (
            <div className="sidebar-widget category-stats">
              <h3>🏷️ 文章分类</h3>
              <div className="category-list">
                {renderCategoryStats()}
              </div>
            </div>
          )}
        </div>

        {/* 主要内容 */}
        <div className="user-main">
          {/* 导航标签 */}
          <div className="user-tabs">
            <button 
              className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              概览
            </button>
            <button 
              className={`tab-btn ${activeTab === 'posts' ? 'active' : ''}`}
              onClick={() => setActiveTab('posts')}
            >
              文章 ({userPosts.length})
            </button>
            <button 
              className={`tab-btn ${activeTab === 'drafts' ? 'active' : ''}`}
              onClick={() => setActiveTab('drafts')}
            >
              草稿箱
            </button>
            <button 
              className={`tab-btn ${activeTab === 'popular' ? 'active' : ''}`}
              onClick={() => setActiveTab('popular')}
            >
              热门文章
            </button>
          </div>

          {/* 标签内容 */}
          <div className="tab-content">
            {activeTab === 'overview' && (
              <>
                <div className="user-bio">
                  <h3>关于我</h3>
                  <p className="bio-text">
                    {userData.bio || '这个人很懒，还没有写个人简介...'}
                  </p>
                </div>

                {recentPosts.length > 0 && (
                  <div className="recent-section">
                    <div className="section-header">
                      <h3>最近的文章</h3>
                      <button 
                        className="view-all-btn"
                        onClick={() => setActiveTab('posts')}
                      >
                        查看全部 →
                      </button>
                    </div>
                    <div className="recent-posts">
                      {recentPosts.map((post) => (
                        <div 
                          key={post.id} 
                          className="recent-post" 
                          onClick={() => navigate(`/post/${post.id}`)}
                        >
                          <div className="post-image">
                            <img 
                              src={post.coverImage} 
                              alt={post.title}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=800&auto=format&fit=crop';
                              }}
                            />
                          </div>
                          <div className="recent-post-content">
                            <h4 className="post-title">{post.title}</h4>
                            <p className="post-excerpt">{post.excerpt || '点击阅读全文'}</p>
                            <div className="post-meta">
                              <span className="post-date">
                                {formatDate(post.publishDate)}
                              </span>
                              <span className="post-stats">
                                <span className="stat-item">❤️ {post.likes || 0}</span>
                                <span className="stat-item">👁️ {post.views || 0}</span>
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 热门文章预览 */}
                <div className="popular-section">
                  <div className="section-header">
                    <h3>热门文章</h3>
                    <button 
                      className="view-all-btn"
                      onClick={() => setActiveTab('popular')}
                    >
                      查看全部 →
                    </button>
                  </div>
                  <div className="popular-grid">
                    {popularPosts.slice(0, 4).map((post) => (
                      <div 
                        key={post.id} 
                        className="popular-card"
                        onClick={() => navigate(`/post/${post.id}`)}
                      >
                        <div className="popular-card-image">
                          <img 
                            src={post.coverImage} 
                            alt={post.title}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=800&auto=format&fit=crop';
                            }}
                          />
                        </div>
                        <div className="popular-card-content">
                          <h4 className="popular-title">{post.title}</h4>
                          <div className="popular-meta">
                            <span className="popular-views">👁️ {post.views || 0}</span>
                            <span className="popular-likes">❤️ {post.likes || 0}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {activeTab === 'posts' && (
              <div className="user-posts-section">
                <div className="section-header">
                  <h3>我的文章 ({userPosts.length}篇)</h3>
                  <button 
                    className="new-post-btn"
                    onClick={() => navigate('/write')}
                  >
                    <span className="btn-icon">✏️</span>
                    写新文章
                  </button>
                </div>
                
                {userPosts.length > 0 ? (
                  <div className="posts-list">
                    {userPosts.map((post) => (
                      <div 
                        key={post.id} 
                        className="user-post-card"
                        onClick={() => navigate(`/post/${post.id}`)}
                      >
                        <div className="post-header">
                          <h4 className="post-title">{post.title}</h4>
                          <span className="post-category">{post.category}</span>
                        </div>
                        <p className="post-excerpt">{post.excerpt}</p>
                        <div className="post-footer">
                          <span className="post-date">
                            {formatDate(post.publishDate)}
                          </span>
                          <div className="post-stats">
                            <span className="stat-item">❤️ {post.likes || 0}</span>
                            <span className="stat-item">👁️ {post.views || 0}</span>
                            <span className="stat-item">💬 {post.comments || 0}</span>
                          </div>
                          {/* 新增：删除按钮 */}
                          <button 
                            className="delete-post-btn"
                            onClick={(e) => handleDeleteClick(post, e)}
                            disabled={deletingPostId === post.id}
                          >
                            {deletingPostId === post.id ? (
                              <span className="delete-loading">⏳</span>
                            ) : (
                              <span className="delete-icon">🗑️</span>
                            )}
                            删除
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-posts">
                    <div className="no-posts-cat">📝</div>
                    <h4>还没有写过文章</h4>
                    <p>开始分享你的猫咪故事吧！</p>
                    <button 
                      className="start-writing-btn"
                      onClick={() => navigate('/write')}
                    >
                      开始写作
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'drafts' && (
              <div className="drafts-section">
                <div className="empty-state">
                  <div className="empty-cat">📄</div>
                  <h4>暂无草稿</h4>
                  <p>这里会保存你的未发布文章</p>
                  <button 
                    className="new-draft-btn"
                    onClick={() => navigate('/write')}
                  >
                    创建新草稿
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'popular' && (
              <div className="popular-articles-section">
                <div className="section-header">
                  <h3>热门文章</h3>
                  <span className="section-subtitle">最受欢迎的猫咪文章</span>
                </div>
                <div className="popular-articles-grid">
                  {popularPosts.map((post, index) => (
                    <div 
                      key={post.id} 
                      className="popular-article-card"
                      onClick={() => navigate(`/post/${post.id}`)}
                    >
                      <div className="article-rank">
                        <span className="rank-number">{index + 1}</span>
                      </div>
                      <div className="article-image">
                        <img 
                          src={post.coverImage} 
                          alt={post.title}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=800&auto=format&fit=crop';
                          }}
                        />
                      </div>
                      <div className="article-content">
                        <h4 className="article-title">{post.title}</h4>
                        <p className="article-excerpt">{post.excerpt}</p>
                        <div className="article-meta">
                          <span className="article-category">{post.category}</span>
                          <div className="article-stats">
                            <span className="stat-item">👁️ {post.views || 0}</span>
                            <span className="stat-item">❤️ {post.likes || 0}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* 新增：删除确认弹窗 */}
          {showDeleteConfirm && (
            <div className="delete-confirm-modal">
              <div className="modal-overlay" onClick={cancelDelete}></div>
              <div className="modal-content">
                <h3 className="modal-title">确认删除</h3>
                <p className="modal-message">
                  你确定要删除文章《{postToDelete?.title}》吗？
                  <br />
                  <span className="warning-text">此操作不可撤销！</span>
                </p>
                <div className="modal-actions">
                  <button 
                    className="modal-cancel-btn"
                    onClick={cancelDelete}
                    disabled={deletingPostId !== null}
                  >
                    取消
                  </button>
                  <button 
                    className="modal-confirm-btn"
                    onClick={confirmDeletePost}
                    disabled={deletingPostId !== null}
                  >
                    {deletingPostId !== null ? '删除中...' : '确认删除'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserCenter;